export const APP_CONFIG = {
  NAME: 'Civista',
  DESCRIPTION: 'Revolutionary UK Benefits Guidance Platform',
  VERSION: '1.0.0',
  
  // Environment
  ENVIRONMENT: import.meta.env.MODE as 'development' | 'production' | 'test',
  IS_DEVELOPMENT: import.meta.env.DEV,
  IS_PRODUCTION: import.meta.env.PROD,
  
  // Features
  FEATURES: {
    ENABLE_PWA: true,
    ENABLE_ANALYTICS: true,
    ENABLE_ERROR_TRACKING: true,
    ENABLE_PERFORMANCE_MONITORING: true,
    ENABLE_ACCESSIBILITY_FEATURES: true,
    ENABLE_VOICE_INPUT: true,
    ENABLE_OFFLINE_MODE: true,
    ENABLE_DARK_MODE: false, // Not implemented yet
  },
  
  // UI
  UI: {
    ANIMATION_DURATION: 300,
    DEBOUNCE_DELAY: 500,
    INFINITE_SCROLL_THRESHOLD: 0.8,
    MAX_MOBILE_WIDTH: 768,
    MAX_TABLET_WIDTH: 1024,
  },
  
  // Storage
  STORAGE: {
    KEYS: {
      AUTH_TOKEN: 'civista_auth_token',
      USER_PREFERENCES: 'civista_user_preferences',
      CONSENT_STATUS: 'civista_consent_status',
      THEME_PREFERENCE: 'civista_theme',
      CALCULATOR_DRAFT: 'civista_calculator_draft',
      CHAT_DRAFT: 'civista_chat_draft',
    },
    TOKEN_EXPIRY: 7200000, // 2 hours in milliseconds
  },
  
  // Validation
  VALIDATION: {
    PASSWORD_MIN_LENGTH: 12,
    EMAIL_MAX_LENGTH: 255,
    NAME_MAX_LENGTH: 100,
    MESSAGE_MAX_LENGTH: 4000,
    FILE_MAX_SIZE: 10 * 1024 * 1024, // 10MB
    SUPPORTED_FILE_TYPES: ['pdf', 'doc', 'docx', 'txt', 'jpg', 'jpeg', 'png'],
  },
  
  // Accessibility
  ACCESSIBILITY: {
    WCAG_LEVEL: 'AAA',
    FOCUS_TRAP_ENABLED: true,
    KEYBOARD_NAVIGATION_ENABLED: true,
    SCREEN_READER_ANNOUNCEMENTS: true,
    HIGH_CONTRAST_SUPPORT: true,
    REDUCED_MOTION_SUPPORT: true,
  },
  
  // Performance
  PERFORMANCE: {
    BUNDLE_SIZE_LIMIT: 1024 * 1024, // 1MB
    LIGHTHOUSE_THRESHOLD: 90,
    LAZY_LOADING_ENABLED: true,
    IMAGE_OPTIMIZATION_ENABLED: true,
    CODE_SPLITTING_ENABLED: true,
  },
  
  // UK-specific configuration
  UK: {
    REGIONS: [
      'london',
      'south_east',
      'south_west',
      'east_of_england',
      'west_midlands',
      'east_midlands',
      'yorkshire_humber',
      'north_west',
      'north_east',
      'scotland',
      'wales',
      'northern_ireland',
    ] as const,
    BENEFIT_TYPES: [
      'universal_credit',
      'housing_benefit',
      'child_benefit',
      'child_tax_credit',
      'working_tax_credit',
      'income_support',
      'jobseekers_allowance',
      'employment_support_allowance',
      'pension_credit',
      'disability_living_allowance',
      'personal_independence_payment',
      'attendance_allowance',
    ] as const,
    COUNCIL_TAX_BANDS: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const,
    POSTCODE_REGEX: /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i,
    CURRENCY_SYMBOL: '£',
    DATE_FORMAT: 'dd/MM/yyyy',
    TIME_FORMAT: 'HH:mm',
  },
  
  // External services
  EXTERNAL: {
    SUPPORT_EMAIL: 'support@civista.co.uk',
    PRIVACY_EMAIL: 'privacy@civista.co.uk',
    SECURITY_EMAIL: 'security@civista.co.uk',
    CRISIS_HELPLINES: {
      SAMARITANS: '116 123',
      SHOUT: 'Text SHOUT to 85258',
      MIND: '0300 123 3393',
      CITIZENS_ADVICE: '0800 144 8848',
    },
    FEEDBACK_URL: import.meta.env.VITE_FEEDBACK_URL || 'https://feedback.civista.co.uk',
    STATUS_PAGE: import.meta.env.VITE_STATUS_PAGE_URL || 'https://status.civista.co.uk',
  },
} as const;

export type Region = typeof APP_CONFIG.UK.REGIONS[number];
export type BenefitType = typeof APP_CONFIG.UK.BENEFIT_TYPES[number];
export type CouncilTaxBand = typeof APP_CONFIG.UK.COUNCIL_TAX_BANDS[number];